import styled, { css } from 'styled-components';
import CircularProgress from '@mui/material/CircularProgress';
import OvButton from './OvButton';
import Variables from '../../../design-system/variables';
import Colours from '../../../design-system/colours';
import React, { ReactElement, ReactNode } from 'react';
import { ButtonProps } from '@mui/material/Button';
import { CompactButtonTone } from '../../../common/model/type/compact-button-tone.type';

interface OvCompactButtonProps {
  icon?: ReactElement;
  children?: ReactNode;
  tone?: CompactButtonTone;
  isBusy?: boolean;
  busyLabel?: ReactNode;
}

const OvCompactButton: React.FC<OvCompactButtonProps & ButtonProps> = ({
  icon,
  children,
  tone,
  isBusy = false,
  busyLabel,
  disabled,
  ...props
}) => {
  const renderIcon = () => {
    if (isBusy) {
      return (
        <IconWrapper>
          <CircularProgress size="0.875rem" color="inherit" aria-hidden />
        </IconWrapper>
      );
    }

    return icon ? <IconWrapper>{icon}</IconWrapper> : '';
  };

  return (
    <StyledOvButton
      {...props}
      $tone={tone}
      data-tone={tone}
      disabled={disabled || isBusy}
      aria-busy={isBusy || undefined}
    >
      {renderIcon()}
      {isBusy && busyLabel ? busyLabel : children}
    </StyledOvButton>
  );
};

export default OvCompactButton;

const getToneStyles = (tone: CompactButtonTone) => {
  switch (tone) {
    case 'primary':
      return css`
        background-color: ${Colours.OV_BASE};
        border-color: ${Colours.OV_BASE};
        color: ${Colours.WHITE};

        &:hover {
          background-color: ${Colours.WHITE};
          color: ${Colours.OV_BASE};
        }
      `;

    case 'secondary':
      return css`
        background-color: ${Colours.WHITE};
        border-color: ${Colours.OV_BASE};
        color: ${Colours.OV_BASE};

        &:hover {
          background-color: ${Colours.OV_WHITE_HOVER};
          color: ${Colours.OV_BASE};
        }
      `;

    case 'danger':
      return css`
        background-color: ${Colours.OV_ERROR_ACCESSIBLE};
        border-color: ${Colours.OV_ERROR_ACCESSIBLE};
        color: ${Colours.WHITE};

        &:hover {
          background-color: ${Colours.WHITE};
          color: ${Colours.OV_ERROR_ACCESSIBLE};
        }
      `;

    default: {
      const unhandledTone: never = tone;

      return unhandledTone;
    }
  }
};

const StyledOvButton = styled(OvButton)<{ $tone?: CompactButtonTone }>`
  && {
    display: flex;
    align-items: center;
    text-transform: none;
    padding: 2px 0.625rem 2px 0.25rem;
    border-radius: ${Variables.borderRadius.CLINIC_DASHBOARD_LARGE};
    background-color: ${Colours.OV_BASE};

    &:hover {
      a {
        color: ${Colours.OV_BASE};
      }
    }

    a {
      border-radius: ${Variables.borderRadius.CLINIC_DASHBOARD_LARGE};
      padding: 0.25rem 1rem;
      text-decoration: none;
      text-transform: none;
      font-weight: bold;
      font-size: 0.75rem;
      color: ${Colours.OV_WHITE};
    }

    ${({ $tone }) =>
      $tone &&
      css`
        padding: 0.25rem 1rem;
        border-width: 2px;
        font-size: ${Variables.fontSizes.SMALL};
        font-weight: bold;
        line-height: ${Variables.lineHeights.LARGE};
        white-space: nowrap;
        ${getToneStyles($tone)}

        &:focus-visible {
          outline: 3px solid ${Colours.OV_ACCENT_BLUE};
          outline-offset: 2px;
        }

        &.Mui-disabled {
          background-color: ${Colours.OV_BORDER_COLOR_V3};
          border-color: ${Colours.OV_BORDER_COLOR_V3};
          color: ${Colours.OV_CHARCOAL};
        }
      `}
  }
`;

const IconWrapper = styled.span`
  display: flex;
  align-items: center;
  margin-right: 0.5rem;
  margin-left: 0.25rem;
`;
